import { inspectionsService } from '@/services/inspections';
import type { SaveTechnicalOpinionPayload } from '@/services/inspections/types';
import { s3Service } from '@/services/s3';
import type { UploadedFile } from '@/shared/components/form-fields/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface FollowUpInspectionInput extends Omit<
  SaveTechnicalOpinionPayload,
  'attachments'
> {
  attachments?: UploadedFile[];
}

export function useFollowUpInspection(policyId: string, inspectionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ attachments, ...rest }: FollowUpInspectionInput) => {
      const prefix = `inspections/follow-up/${inspectionId}`;
      const attachmentKeys = await Promise.all(
        (attachments ?? []).map(uploaded =>
          s3Service.uploadFile(uploaded.file, prefix)
        )
      );

      const payload: SaveTechnicalOpinionPayload = {
        ...rest,
        ...(attachmentKeys.length > 0 && { attachments: attachmentKeys }),
      };

      const response = await inspectionsService.saveTechnicalOpinion(
        policyId,
        inspectionId,
        payload
      );

      if (!response.success) {
        throw new Error(
          response.error ||
          'Erro ao registrar follow-up, tente novamente mais tarde'
        );
      }

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['inspections'] });
      await queryClient.invalidateQueries({ queryKey: ['inspections-summary'] });
      await queryClient.invalidateQueries({ queryKey: ['inspection-records'] });
      await queryClient.invalidateQueries({
        queryKey: ['policy-inspections', policyId],
      });
      await queryClient.refetchQueries({
        queryKey: ['inspection-records', policyId, inspectionId],
      });
      await queryClient.refetchQueries({
        queryKey: ['policy-inspections', policyId],
      });
    },
  });
}
